import React from "react";
import { AppWindow, Building2, Cloud, Globe, Laptop, Network, Shield, Waypoints, type LucideIcon } from "lucide-react";
import type { Fwaas } from "@shared/components/fwaas/FwaasData";
import type { HaMember } from "kontrolEdge/components/Shared/VmSpecsCard";
import type { FwaasOverview } from "../overview/overview.types";
import { LANE_LABEL, haPair, type AlertLevel, type Lane, type MapNode, type MapSelection, type NodeIcon } from "./serviceMap";
import { ALERT_TONE } from "./alertTone";

export const ICON: Record<NodeIcon, LucideIcon> = {
    internet: Globe,
    published: AppWindow,
    remote: Laptop,
    "site-to-site": Waypoints,
    fwint: Building2,
    cloud: Cloud,
    other: Network,
};

const LANES: Lane[] = ["public", "private"];

const ROW =
    "flex w-full items-center gap-3 rounded-xl border bg-semantic-bg-primary px-3 py-2.5 text-left transition-shadow duration-200";

/**
 * The service map on a phone (CTX-1479): the drawing needs 760 px, so below `sm` the
 * firewall and each side's connections read as a list instead. Every row is the same
 * selection as its card on the drawing and opens the same inspector, under the list.
 */
export default function ServiceMapList({
    service,
    overview,
    haMembers,
    firewallAlert,
    nodes,
    selection,
    onToggle,
}: {
    service: Fwaas;
    overview: FwaasOverview | null;
    haMembers: HaMember[];
    firewallAlert: AlertLevel | null;
    nodes: MapNode[];
    selection: MapSelection | null;
    onToggle: (key: MapSelection) => void;
}) {
    const ha = overview?.ha;
    const pair = haPair(ha, haMembers);
    const sync = ha?.in_sync == null ? "HA" : ha.in_sync ? "HA in sync" : "HA out of sync";
    const mode =
        ha?.mode === "active-passive"
            ? "active / passive"
            : ha?.mode === "active-active"
              ? "active / active"
              : ha?.mode === "standalone"
                ? "standalone"
                : null;
    const id = service.subscriptionId ?? service.name;
    const pressedRing = "ring-4 ring-semantic-bg-brand-secondary border-semantic-border-brand-primary";

    return (
        <div className="flex flex-col gap-4 px-3 pb-3.5 sm:hidden">
            <button
                type="button"
                aria-pressed={selection === "firewall"}
                onClick={() => onToggle("firewall")}
                className={`${ROW} border-[1.5px] ${
                    firewallAlert ? ALERT_TONE[firewallAlert].border : "border-semantic-border-brand-primary"
                } ${selection === "firewall" ? pressedRing : ""}`}
            >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-semantic-bg-brand-bold text-semantic-text-inverse-primary">
                    <Shield size={18} aria-hidden />
                </span>
                <span className="flex min-w-0 flex-col">
                    <span className="truncate font-mono text-sm">{id}</span>
                    <span className="truncate text-xs text-semantic-text-secondary">
                        {["Virtual firewall", mode, pair.length > 0 ? sync : null].filter(Boolean).join(" · ")}
                    </span>
                </span>
            </button>

            {LANES.map((lane) => {
                const laneNodes = nodes.filter((n) => n.lane === lane);
                const key: MapSelection = `lane:${lane}`;
                return (
                    <div key={lane} className="flex flex-col gap-2">
                        <button
                            type="button"
                            aria-pressed={selection === key}
                            onClick={() => onToggle(key)}
                            className={`inline-flex min-h-11 items-center gap-2 self-start rounded-full px-2 text-xs font-semibold uppercase tracking-wider ${
                                selection === key ? "bg-semantic-bg-brand-secondary text-semantic-text-brand" : "text-semantic-text-secondary"
                            }`}
                        >
                            {LANE_LABEL[lane]}
                            <span className="rounded-full border border-semantic-border-primary bg-semantic-bg-primary px-1.5 font-mono text-[11px] normal-case tracking-normal">
                                {laneNodes.length}
                            </span>
                        </button>
                        {laneNodes.length === 0 ? (
                            <p className="px-2 text-xs text-semantic-text-secondary">Nothing on this side.</p>
                        ) : (
                            <ul className="flex flex-col gap-2">
                                {laneNodes.map((node) => {
                                    const Icon = ICON[node.icon];
                                    const tone = node.alert ? ALERT_TONE[node.alert] : null;
                                    return (
                                        <li key={node.id}>
                                            <button
                                                type="button"
                                                aria-pressed={selection === node.id}
                                                aria-label={`${node.name}, ${node.meta}${node.alert ? ", with observations" : ""}`}
                                                onClick={() => onToggle(node.id)}
                                                className={`${ROW} ${
                                                    tone ? tone.border : "border-semantic-border-secondary"
                                                } ${selection === node.id ? pressedRing : ""}`}
                                            >
                                                <span
                                                    className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${
                                                        tone ? tone.icon : "bg-semantic-bg-brand-secondary text-semantic-text-brand"
                                                    }`}
                                                >
                                                    <Icon size={16} aria-hidden />
                                                </span>
                                                <span className="flex min-w-0 flex-1 flex-col">
                                                    <span className="truncate text-sm font-semibold leading-tight">{node.name}</span>
                                                    <span
                                                        className={`truncate text-xs ${tone ? tone.text : "text-semantic-text-secondary"}`}
                                                    >
                                                        {node.meta}
                                                    </span>
                                                </span>
                                                <span className="shrink-0 rounded border border-semantic-border-primary px-1.5 font-mono text-[10.5px] text-semantic-text-secondary">
                                                    {node.edgeLabel}
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
